import React, {useContext, useEffect, useState} from 'react';
import {Link, useParams, useRouteMatch} from 'react-router-dom';
import UserContext from '../../context/UserContext';
import CampaignContext from '../../context/CampaignContext';
import {watchCharacter, updateSheet, watchCampaignCharacters} from '../characterRepository';
import {watchRolls} from '../rollRepository';
import {ATTRIBUTES_BY_ID} from '../data/traits';
import {useVtmRollActions} from '../useVtmRollActions';
import {frenzyPoolSize, mendAmount} from '../dice';
import {getRemaining, getWillpowerMax} from '../character';
import {getPowerTraits, findReopenableRoll} from '../rolls';
import {vtmT, currentLanguage} from '../i18n';
import VtmCharacterSheet from './VtmCharacterSheet';
import VtmRollChat from './VtmRollChat';
import VtmCoterie from './VtmCoterie';
import VtmXpModal from './VtmXpModal';
import VtmProfileModal from './VtmProfileModal';
import {getXpAvailable} from '../progression';
import VtmRollModal, {ROLL_MODES} from './VtmRollModal';
import VtmFeedModal from './VtmFeedModal';
import VtmCreationWizard from './creation/VtmCreationWizard';
import {VtmSceneForCharacter} from './VtmScene';
import VtmHungerVeil from './VtmHungerVeil';
import {useRollRequest, VtmRollRequestBanner} from './VtmRollRequest';
import {isRequestPendingFor, REQUEST_MODES} from '../rollRequest';
import VtmStainSplatters from './VtmStainSplatters';
import {hungerTier, useHungerEffectsEnabled} from '../hungerEffects';
import {CHARACTER_STATUS} from '../character';
import '../../styles/vtm/roll.css';

const ICONS = {
  rouse: <path d="M12 3 C 12 3, 5 11.5, 5 15.5 a 7 7 0 0 0 14 0 C 19 11.5, 12 3, 12 3 Z" />,
  remorse: <path d="M12 20 C 6 15.5, 3 12.5, 3 8.8 A 4.3 4.3 0 0 1 12 6.5 A 4.3 4.3 0 0 1 21 8.8 C 21 12.5, 18 15.5, 12 20 Z M12 6.5 L10.5 10.5 L13.5 13 L11.5 17" />,
  frenzy: <path d="M6 4 L9 20 M11 3 L13.5 20 M16 4 L18 18" />,
  mend: <path d="M9 3 H15 V9 H21 V15 H15 V21 H9 V15 H3 V9 H9 Z" />,
  feed: <path d="M8 3 L9.5 13 L11 3 M13 3 L14.5 13 L16 3 M9.5 13 C 9.5 17, 12 21, 12 21 C 12 21, 14.5 17, 14.5 13" />,
};

const TestTile = ({icon, title, fullTitle, meta, onClick, disabled}) => (
  <button type="button" className="vtmTestTile" onClick={onClick} disabled={disabled} title={fullTitle}>
    <svg className="vtmTestIcon" viewBox="0 0 24 24" aria-hidden="true">{ICONS[icon]}</svg>
    <span className="vtmTestTitle">{title}</span>
    <span className="vtmTestMeta">{meta}</span>
  </button>
);

/** Action d'entretien (soin, repas) : plus discrète que les tuiles de tests. */
const CareAction = ({icon, title, meta, hint, onClick, disabled}) => (
  <button type="button" className="vtmCareAction" onClick={onClick} disabled={disabled} title={hint}>
    <svg className="vtmTestIcon" viewBox="0 0 24 24" aria-hidden="true">{ICONS[icon]}</svg>
    <span className="vtmCareTitle">{title}</span>
    <span className="vtmTestMeta">{meta}</span>
  </button>
);

const PlayArea = ({character, campaign, user, canRoll, dmUrl}) => {
  const actions = useVtmRollActions({character, campaign, user});
  const [rolls, setRolls] = useState([]);
  const [coterie, setCoterie] = useState([]);
  const [modal, setModal] = useState({open: false, mode: ROLL_MODES.POOL, traits: [], power: null, request: null});
  const [feedOpen, setFeedOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [xpOpen, setXpOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const campaignUid = campaign.uid || character.idCampaign;
  const sheet = character.vtm;
  // Effets de Soif : pour le joueur et pour le MJ qui ouvre la fiche (pas pour les autres joueurs)
  const hungerEffects = useHungerEffectsEnabled() && canRoll;
  const request = useRollRequest(campaignUid);
  const requestPending = canRoll && isRequestPendingFor(request, character.uid, rolls);
  const frenzyDice = frenzyPoolSize({willpower: getRemaining(sheet.willpower, getWillpowerMax(sheet)), humanity: sheet.humanity});

  useEffect(() => watchRolls(campaignUid, setRolls), [campaignUid]);
  useEffect(() => watchCampaignCharacters(campaignUid, setCoterie, e => console.log(e)), [campaignUid]);

  const openRoll = (traits = []) => setModal({open: true, mode: ROLL_MODES.POOL, traits, power: null, request: null});
  const onPowerClick = (power) => setModal(power.pool
    ? {open: true, mode: ROLL_MODES.POOL, traits: getPowerTraits(sheet, power.pool), power, request: null}
    : {open: true, mode: ROLL_MODES.ACTIVATE, traits: [], power, request: null});
  const answerRequest = () => setModal({
    open: true,
    mode: request.mode === REQUEST_MODES.FRENZY ? ROLL_MODES.FRENZY : ROLL_MODES.POOL,
    traits: request.traits,
    power: null,
    request,
  });
  // Dernier jet du personnage encore relançable : un clic dans le chat rouvre la fenêtre sur son résultat
  const reopenable = canRoll ? findReopenableRoll(rolls, character.uid) : null;
  const reopenRoll = (record) => setModal({open: true, mode: ROLL_MODES.POOL, traits: [], power: null, request: null, resume: {key: record.key, record}});
  const onTraitClick = (traitId) => openRoll(ATTRIBUTES_BY_ID[traitId] ? [traitId, ''] : ['', traitId]);

  const quick = (action) => async () => {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      console.log(e);
    }
    setBusy(false);
  };

  return (
    <div className={`vtmPlay${hungerEffects ? ` hungerTier${hungerTier(sheet.hunger)}` : ''}`}>
      {hungerEffects && <VtmHungerVeil hunger={sheet.hunger} />}
      {hungerEffects && <VtmStainSplatters stains={sheet.stains || 0} humanity={sheet.humanity} />}
      <div className="vtmPlaySheet">
        <VtmCharacterSheet
          character={character}
          onTraitClick={canRoll ? onTraitClick : undefined}
          onPowerClick={canRoll ? onPowerClick : undefined}
          onSheetChange={canRoll ? (fields => updateSheet(character.uid, fields).catch(e => console.log(e))) : undefined}
          headerTop={dmUrl && <Link className="vtmBackLink" to={dmUrl}>{vtmT('backToDm')}</Link>}
          headerExtra={(
            <>
              <VtmCoterie characters={coterie} currentUid={character.uid} lng={currentLanguage()} />
              {canRoll && (
                <>
                  <button type="button" className="vtmHeaderAction" onClick={() => setXpOpen(true)}>
                    {vtmT('spendXp')} · {getXpAvailable(sheet)}
                  </button>
                  <button type="button" className="vtmHeaderAction subtle" onClick={() => setProfileOpen(true)}>
                    <svg className="vtmHeaderIcon" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M4 20 L4.8 16.2 L15.5 5.5 L18.5 8.5 L7.8 19.2 Z M13.5 7.5 L16.5 10.5" />
                    </svg>
                    {vtmT('editProfile')}
                  </button>
                </>
              )}
            </>
          )}
        />
      </div>
      <aside className="vtmPlayChat">
        <VtmSceneForCharacter campaignUid={campaignUid} characterUid={character.uid} />
        {requestPending && <VtmRollRequestBanner request={request} onRoll={answerRequest} />}
        {canRoll && (
          <div className="vtmQuickActions">
            <button type="button" className="vtmButton vtmFreeRoll" onClick={() => openRoll()}>{vtmT('roll')}</button>
            <div className="vtmTests" role="group" aria-label={vtmT('tests')}>
              <TestTile
                icon="rouse"
                title={vtmT('tileRouse')}
                fullTitle={vtmT('rouseCheck')}
                meta={`${sheet.hunger} / 5`}
                onClick={quick(() => actions.rollRouse())}
                disabled={busy}
              />
              <TestTile
                icon="remorse"
                title={vtmT('tileRemorse')}
                fullTitle={vtmT('remorseCheck')}
                meta={sheet.stains ? vtmT('stainsCount', {value: sheet.stains}) : vtmT('noStainsShort')}
                onClick={quick(() => actions.rollRemorse())}
                disabled={busy || !sheet.stains}
              />
              <TestTile
                icon="frenzy"
                title={vtmT('tileFrenzy')}
                fullTitle={vtmT('frenzyCheck')}
                meta={vtmT('diceCount', {value: frenzyDice})}
                onClick={() => setModal({open: true, mode: ROLL_MODES.FRENZY, traits: [], power: null, request: null})}
                disabled={busy}
              />
            </div>
            <div className="vtmCareActions">
              <CareAction
                icon="mend"
                title={vtmT('tileMend')}
                hint={vtmT('mendHint', {value: mendAmount(sheet.bloodPotency)})}
                meta={sheet.health.superficial ? vtmT('superficialCount', {value: sheet.health.superficial}) : vtmT('noWounds')}
                onClick={quick(() => actions.mend())}
                disabled={busy || !sheet.health.superficial}
              />
              <CareAction
                icon="feed"
                title={vtmT('tileFeed')}
                meta={vtmT('hungerShort', {value: sheet.hunger})}
                onClick={() => setFeedOpen(true)}
                disabled={busy || !sheet.hunger}
              />
            </div>
          </div>
        )}
        <VtmRollChat rolls={rolls} userUid={user.uid} dmUid={campaign.idUserDm} reopenKey={reopenable?.key} onReopen={reopenRoll} />
      </aside>
      {canRoll && (
        <>
          <VtmXpModal isOpen={xpOpen} onClose={() => setXpOpen(false)} character={character} actions={actions} />
          <VtmProfileModal isOpen={profileOpen} onClose={() => setProfileOpen(false)} character={character} />
          <VtmFeedModal isOpen={feedOpen} onClose={() => setFeedOpen(false)} character={character} actions={actions} />
        </>
      )}
      {canRoll && (
        <VtmRollModal
          isOpen={modal.open}
          mode={modal.mode}
          initialTraits={modal.traits}
          power={modal.power}
          request={modal.request}
          resume={modal.resume}
          onClose={() => setModal({...modal, open: false, resume: null})}
          character={character}
          actions={actions}
        />
      )}
    </div>
  );
};

/** Route /:campaignId/:characterId en mode VtM : charge le personnage en temps réel et contrôle l'accès. */
const VtmCharacterPage = () => {
  const {characterIdUrl} = useParams();
  const match = useRouteMatch();
  const {user} = useContext(UserContext);
  const {campaign} = useContext(CampaignContext);
  const [state, setState] = useState({loading: true, character: null});

  useEffect(() => {
    setState({loading: true, character: null});
    return watchCharacter(
      characterIdUrl,
      character => setState({loading: false, character}),
      () => setState({loading: false, character: null}),
    );
  }, [characterIdUrl]);

  if (state.loading) {
    return <p className="vtmPageMessage">{vtmT('loading')}</p>;
  }
  if (!state.character || !state.character.vtm) {
    return <p className="vtmPageMessage">{vtmT('notFound')}</p>;
  }
  const isOwner = state.character.idUser === user.uid;
  const isDm = campaign.idUserDm === user.uid;
  if (!isOwner && !isDm) {
    return <p className="vtmPageMessage">{vtmT('forbidden')}</p>;
  }
  // La fiche est à /campaigns/:campaignId/:characterId, la vue MJ à /campaigns/:campaignId/dm
  const dmUrl = isDm ? match.url.replace(/\/[^/]+\/?$/, '/dm') : null;
  if (state.character.status === CHARACTER_STATUS.DRAFT) {
    // Le joueur poursuit sa création ; le MJ voit le brouillon en lecture seule
    return isOwner
      ? <VtmCreationWizard key={state.character.uid} character={state.character} />
      : <VtmCharacterSheet character={state.character} headerTop={<Link className="vtmBackLink" to={dmUrl}>{vtmT('backToDm')}</Link>} />;
  }
  return <PlayArea character={state.character} campaign={campaign} user={user} canRoll={isOwner || isDm} dmUrl={dmUrl} />;
};

export default VtmCharacterPage;
